import { buildHeaders, getModuleDetails } from "./api";
import { productionSceneRequest } from "./productionScene";

const base = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");
async function request(projectId, path, token, { body, method = "GET", file, signal } = {}) {
  if (!base || !projectId) throw new Error("Open a saved project with a configured API first.");
  const headers = await buildHeaders(token, body && !file ? { "Content-Type": "application/json" } : {});
  const response = await fetch(`${base}/pv/uncertainty/${encodeURIComponent(projectId)}${path}`, {
    method, headers, body: file || (body ? JSON.stringify(body) : undefined), signal,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(typeof error?.detail === "string" ? error.detail : `HTTP ${response.status}`);
  }
  return response;
}
export async function fetchPvUncertainty(projectId, token, signal) {
  return (await request(projectId, "/jobs", token, { signal })).json();
}
export async function fetchPvFinancialReadiness(projectId, config, token, signal) {
  return (await request(projectId, "/financial-readiness", token, { method: "POST", body: { config }, signal })).json();
}
export async function fetchPvSamplingDiagnostics(projectId, jobId, token, signal) {
  return (await request(projectId, `/jobs/${encodeURIComponent(jobId)}/sampling`, token, { signal })).json();
}
export async function fetchPvWeatherSummary(projectId, config, token, signal) {
  return (await request(projectId, "/weather/summary", token, { method: "POST", body: { config }, signal })).json();
}
export async function fetchPvUncertaintyFingerprint(projectId, config, token) {
  return (await (await request(projectId, "/fingerprint", token, { method: "POST", body: { config } })).json()).fingerprint;
}
export async function startPvUncertainty(projectId, kind, config, token) {
  const body = kind === "production"
    ? await productionSceneRequest(config, { projectId, loadModule: (name) => getModuleDetails(name, token) })
    : { config };
  return (await request(projectId, `/jobs/${kind}`, token, { method: "POST", body })).json();
}
export async function cancelPvUncertainty(projectId, jobId, token) {
  return (await request(projectId, `/jobs/${encodeURIComponent(jobId)}/cancel`, token, { method: "POST" })).json();
}
export async function uploadPvHistory(projectId, config, file, token) {
  const form = new FormData();
  form.append("config", JSON.stringify(config));
  form.append("file", file);
  return (await request(projectId, "/weather", token, { method: "POST", file: form })).json();
}
// The reader's own clock for the run stamp in the PDF footer. The server has
// no way to know it - the analysis ran hours earlier in a worker, and the
// download arrives over an API with no locale of its own - so the browser
// states it. An environment without Intl, or one that returns nothing, simply
// leaves it out and the footer stays in UTC.
function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export async function downloadPvUncertainty(projectId, jobId, token, format = "xlsx", language = "en", reportBranding = {}) {
  const response = await request(projectId, `/jobs/${encodeURIComponent(jobId)}/download?format=${format}&language=${language}`, token,
    format === "pdf"
      ? { method: "POST", body: { report_branding: reportBranding, report_timezone: browserTimeZone() } }
      : {});
  return response.blob();
}
